import React, { useEffect, useRef, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Secao from '../../components/ui/Secao';
import { Input, Select } from '../../components/ui/Input';
import { notasEntradaApi, ordensCompraApi } from '../../api/compras';
import { depositosApi } from '../../api/estoque';
import ProdutoSearchModal from '../../components/ProdutoSearchModal';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

export default function NotaEntradaForm({ onVoltar, onSalvo }) {
  useTituloPagina('Nova Nota de Entrada');

  const inputRef = useRef(null);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState(null); // resultado do preview
  const [ordemCompraId, setOrdemCompraId] = useState('');
  const [ordensDoFornecedor, setOrdensDoFornecedor] = useState([]);
  const [depositoId, setDepositoId] = useState('');
  const [depositos, setDepositos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [buscandoItemIdx, setBuscandoItemIdx] = useState(null);

  useEffect(() => {
    depositosApi.listar().then(lista => {
      setDepositos(lista);
      const padrao = lista.find(d => d.padrao);
      if (padrao) setDepositoId(padrao.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!dados?.fornecedor_id) { setOrdensDoFornecedor([]); return; }
    ordensCompraApi.listar({ fornecedor_id: dados.fornecedor_id }).then(lista => {
      setOrdensDoFornecedor(lista.filter(o => ['aberta', 'parcial'].includes(o.status)));
    }).catch(() => {});
  }, [dados?.fornecedor_id]);

  const lerArquivo = async (arquivo) => {
    if (!arquivo) return;
    setLendo(true);
    setErro('');
    try {
      const resultado = await notasEntradaApi.preview(arquivo);
      setDados(resultado);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const atualizarItem = (idx, campo, valor) => {
    setDados(d => ({ ...d, itens: d.itens.map((item, i) => i === idx ? { ...item, [campo]: valor } : item) }));
  };

  const vincularProduto = (idx, produto) => {
    setDados(d => ({
      ...d,
      itens: d.itens.map((item, i) => i === idx ? {
        ...item,
        produto_id: produto.id,
        produto_nome: produto.nome,
        produto_codigo: produto.codigo,
        produto_codigo_fabricante: produto.codigo_fabricante
      } : item)
    }));
    setBuscandoItemIdx(null);
  };

  const opcoesOrdens = [{ value: '', label: 'Selecione...' }, ...ordensDoFornecedor.map(o => ({ value: o.id, label: `#${o.numero} — criada em ${new Date(o.criado_em).toLocaleDateString('pt-BR')}` }))];
  const opcoesDepositos = depositos.map(d => ({ value: d.id, label: d.nome }));

  const todosItensResolvidos = dados?.itens.every(i => i.produto_id) ?? false;

  const confirmar = async () => {
    if (!ordemCompraId) { setErro('Selecione a Ordem de Compra vinculada a essa nota'); return; }
    if (!depositoId) { setErro('Selecione o depósito de entrada'); return; }
    if (!todosItensResolvidos) { setErro('Todo item precisa estar vinculado a um produto do catálogo'); return; }
    setSalvando(true);
    setErro('');
    try {
      await notasEntradaApi.criar({
        ordem_compra_id: ordemCompraId,
        deposito_id: depositoId,
        chave_acesso: dados.chave_acesso,
        numero: dados.numero,
        serie: dados.serie,
        data_emissao: dados.data_emissao,
        valor_total: dados.valor_total,
        fornecedor_id: dados.fornecedor_id,
        nome_arquivo_xml: dados.nome_arquivo_xml,
        xml_conteudo: dados.xml_conteudo,
        itens: dados.itens
      });
      if (onSalvo) onSalvo();
      onVoltar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 14 }}>
          <Btn variante="ghost" onClick={onVoltar}>Voltar</Btn>
          {dados && <Btn onClick={confirmar} disabled={salvando}>{salvando ? 'Dando entrada...' : 'Confirmar Entrada'}</Btn>}
        </div>

        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}

        {!dados && (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); lerArquivo(e.dataTransfer.files[0]); }}
            style={{
              border: `2px dashed ${COLORS.border}`, borderRadius: 10, padding: 40, textAlign: 'center',
              cursor: 'pointer', color: COLORS.textMid, fontSize: 14
            }}
          >
            {lendo ? 'Lendo XML...' : 'Clique ou arraste o arquivo XML da NF-e aqui'}
            <input ref={inputRef} type="file" accept=".xml" style={{ display: 'none' }} onChange={e => lerArquivo(e.target.files[0])} />
          </div>
        )}

        {dados && (
          <>
            <Secao titulo="Dados da Nota">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <Input label="Número" value={dados.numero || '-'} disabled />
                <Input label="Série" value={dados.serie || '-'} disabled />
                <Input label="Data de Emissão" value={dados.data_emissao || '-'} disabled />
                <Input label="Valor Total" value={`R$ ${Number(dados.valor_total || 0).toFixed(2)}`} disabled />
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                {dados.fornecedor_id ? (
                  <Badge tom="sucesso">Fornecedor identificado: {dados.fornecedor_nome_xml}</Badge>
                ) : (
                  <Badge tom="perigo">Fornecedor "{dados.fornecedor_nome_xml}" (CNPJ {dados.fornecedor_cnpj_xml}) não está cadastrado — cadastre-o antes de continuar.</Badge>
                )}
              </div>
            </Secao>

            <div style={{ height: 14 }} />

            <Secao titulo="Vínculo com Ordem de Compra">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxWidth: 600 }}>
                <Select label="Ordem de Compra" options={opcoesOrdens} value={ordemCompraId} onChange={e => setOrdemCompraId(e.target.value)} />
                <Select label="Depósito de Entrada" options={opcoesDepositos} value={depositoId} onChange={e => setDepositoId(e.target.value)} />
              </div>
              {dados.fornecedor_id && ordensDoFornecedor.length === 0 && (
                <p style={{ fontSize: 13, color: COLORS.warning, marginTop: 8 }}>Esse fornecedor não tem nenhuma ordem de compra aberta. Crie uma ordem de compra primeiro.</p>
              )}
            </Secao>

            <div style={{ height: 14 }} />

            <Secao titulo="Itens da Nota">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dados.itens.map((item, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr auto 1fr 1fr', gap: 8, alignItems: 'end', padding: 8, background: item.produto_id ? 'transparent' : COLORS.warningBg, borderRadius: 8 }}>
                    <div style={{ fontSize: 12, color: COLORS.textMid }}>
                      <div><strong>Cód. NF: {item.codigo_nf || '-'}</strong></div>
                      <div>{item.descricao_nf}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, marginBottom: 3 }}>Produto no Catálogo</div>
                      {item.produto_id ? (
                        <div style={{ fontSize: 13, color: COLORS.text }}>
                          <div><strong>{item.produto_codigo}</strong> — {item.produto_nome}</div>
                          <div style={{ fontSize: 12, color: COLORS.textMid }}>
                            {item.produto_codigo_fabricante ? `Cód. Fabricante: ${item.produto_codigo_fabricante}` : 'Sem código de fabricante cadastrado'}
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: COLORS.warning, fontWeight: 600 }}>Não vinculado — verificar</div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setBuscandoItemIdx(idx)}
                      title="Buscar e vincular produto"
                      style={{
                        width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `1.5px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.bg, cursor: 'pointer', color: COLORS.textMid
                      }}
                    >
                      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                    </button>

                    <Input label="Quantidade" type="number" value={item.quantidade} onChange={e => atualizarItem(idx, 'quantidade', e.target.value)} />
                    <Input label="Valor Unitário R$" type="number" step="0.01" value={item.valor_unitario} onChange={e => atualizarItem(idx, 'valor_unitario', e.target.value)} />
                  </div>
                ))}
              </div>
            </Secao>

            {buscandoItemIdx !== null && (
              <ProdutoSearchModal
                onSelecionar={(produto) => vincularProduto(buscandoItemIdx, produto)}
                onFechar={() => setBuscandoItemIdx(null)}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
